import { ThermostatBrand } from '@/types/brand'

/**
 * Returns the thermostat brand list narrowed to the brands the program being enrolled in
 * actually accepts, so a Nest-only program (e.g. Octopus Energy Texas) never offers Ecobee
 * or Honeywell. This is the thermostat analog of how `useVehicleMakes` applies the program's
 * `excluded_vehicle_make_models` to the vehicle picker.
 *
 * `eligibleBrandNames` is the program's positive allow-list from the backend
 * (`utility_program.eligible_thermostat_brands`, e.g. ["Nest"]). Names are matched
 * case-insensitively against each brand's `id` (backend "Nest" -> id "nest").
 *
 * The restriction only applies inside the enrollment flow — outside it the user may be
 * connecting a thermostat for any reason, so every brand stays available. A missing/null
 * list likewise means "no brand restriction".
 *
 * If the allow-list matches nothing we return the unfiltered list rather than an empty
 * picker, so an unrecognized brand name from the backend degrades to today's behavior
 * instead of a dead end.
 */
export function filterThermostatBrandsForProgram(
  brands: ThermostatBrand[],
  eligibleBrandNames: string[] | null | undefined,
  inFlow: boolean,
): ThermostatBrand[] {
  if (!inFlow || !eligibleBrandNames) {
    return brands
  }
  const eligibleIds = eligibleBrandNames.map((name) => name.toLowerCase())
  const eligibleBrands = brands.filter((brand) =>
    eligibleIds.includes(brand.id.toLowerCase()),
  )
  return eligibleBrands.length > 0 ? eligibleBrands : brands
}
